<template>
  <div
    class="vb-uploader"
    :class="{
      'upload-in-progress': uploadInProgress,
      enabled: enabled,
      'is-dragging': isDragging,
    }"
    @click="initUpload()"
    @dragover.prevent="startDrag()"
    @dragenter.prevent="startDrag()"
    @dragleave.prevent="stopDrag()"
    @dragend.prevent="stopDrag()"
    @drop.prevent="onDrop($event)"
  >
    <div class="bg"></div>
    <slot></slot>
    <input
      type="file"
      class="vb-uploader-input"
      @change="onFilesChanged($event)"
    />
    <circle-progress :value="percent" />
    <div v-show="showIcon" class="hover-icon">
      <i class="ion ion-camera"></i>
    </div>

    <el-dialog
      :close-on-click-modal="false"
      :visible="displayCropper"
      title="Crop image"
      append-to-body
      :custom-class="crop.customModalClass"
      @close="onDialogClose"
    >
      <div v-loading="uploadInProgress">
        <div :class="{ flex: $slots.preview }">
          <div :class="{ 'w-2/3': $slots.preview }">
            <vue-cropper
              ref="cropper"
              :src="cropImgSrc"
              alt="Source Image"
              v-bind="cropperOptions"
            />
          </div>

          <div
            v-if="$slots.preview"
            class="preview ml-auto"
            :class="{ 'w-1/4': $slots.preview }"
          >
            <h5 class="font-semibold mt-0">
              Preview
            </h5>
            <slot name="preview" />
          </div>
        </div>
        <div class="flex justify-end mt-8">
          <el-button :disabled="uploadInProgress" @click="cancelUploading">
            Cancel
          </el-button>
          <el-button :disabled="uploadInProgress" @click="resetPosition">
            Reset position
          </el-button>
          <el-button
            :disabled="uploadInProgress"
            type="primary"
            @click="cropAndUpload"
          >
            Crop and upload
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FormData from 'formdata-polyfill'
import http from 'services/http'
import { toPairs } from 'lodash'
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'

export default {
  components: {
    VueCropper,
  },
  props: {
    enabled: { type: Boolean, default: true },
    showIcon: { type: Boolean, default: true },
    accept: {
      type: String,
      default: () => '',
    },
    crop: {
      type: Object,
      default: () => ({
        customModalClass: null,
        enabled: false,
        squareCrop: false,
        minHeight: 128,
        minWidth: 128,
        preview: false,
      }),
    },
  },
  data: () => {
    return {
      uploadInProgress: false,
      percent: 0,
      isDragging: false,
      displayCropper: false,
      cropImgSrc: null,
    }
  },
  computed: {
    cropperOptions() {
      const aspectRatio = () => {
        if (this.crop.squareCrop) return 1
        return 16 / 9
      }

      return {
        viewMode: 1,
        dragMode: 'move',
        cropBoxMovable: true,
        minCanvasWidth: this.crop.minWidth,
        minContainerWidth: this.crop.minWidth,
        minCropBoxWidth: this.crop.minWidth,
        minCanvasHeight: this.crop.minHeight,
        minContainerHeight: this.crop.minHeight,
        minCropBoxHeight: this.crop.minHeight,
        preview: this.crop.preview,
        autoCropArea: 1,
        zoomable: false,
      }
    },
  },
  methods: {
    initUpload() {
      if (!this.enabled) return
      this.forceUpload()
    },
    forceUpload() {
      this.$el.querySelector('.vb-uploader-input').click()
    },
    onFilesChanged(event) {
      const file = event.target.files[0]

      if (this.crop.enabled) {
        this.loadImageToCrop(file)
        return
      }
      this.uploadFile(file)
    },
    loadImageToCrop(file) {
      if (file.type.indexOf('image/') === -1) {
        alert('Please select an image file')
        return
      }
      if (typeof FileReader === 'function') {
        const reader = new FileReader()
        reader.onload = (event) => {
          this.cropImgSrc = event.target.result
          // rebuild cropperjs with the updated source
          this.$refs.cropper.replace(event.target.result)
        }
        reader.readAsDataURL(file)
        this.showCropper()
      } else {
        this.$message.error({
          message: 'Something went wrong, please try again',
          duration: 5000,
        })
      }
    },
    clearImage() {
      this.cropImgSrc = null
      this.$refs.cropper.replace(null)
    },
    showCropper() {
      this.displayCropper = true
    },
    async onDialogClose() {
      await this.clearImage()
      this.displayCropper = false
    },
    async cropAndUpload() {
      await this.$refs.cropper.getCroppedCanvas().toBlob((blob) => {
        const file = new File([blob], 'croppedImage')
        this.uploadFile(file)
      })
    },
    resetPosition() {
      this.$refs.cropper.reset()
    },
    zoomTo(points = 1) {
      this.$refs.cropper.zoomTo(points)
    },
    async uploadFile(file) {
      if (!file) {
        return
      }

      const self = this

      // Check file size
      if (file && file.size >= 20971520) {
        this.$message.error({
          message: 'File cannot exceed 20 MB in size!',
          duration: 5000,
        })
        return
      }

      const checkImageSize = (file) => {
        const reader = new FileReader()

        return new Promise((resolve) => {
          reader.addEventListener(
            'load',
            function () {
              const img = new Image()
              img.onload = function () {
                if (this.width * this.height >= 24998140) {
                  resolve(false)
                }
                resolve(true)
              }
              img.src = reader.result
            },
            false,
          )
          reader.readAsDataURL(file)
        })
      }

      if (file && file.type.indexOf('image/') > -1) {
        const isAcceptableSize = await checkImageSize(file)
        if (!isAcceptableSize) {
          self.$message.error({
            message: 'File cannot exceed 25 Megapixel in image size',
            duration: 5000,
          })
          return
        }
      }

      const cloudName = process.env.CLOUDINARY_CLOUD_NAME || 'vestbee'
      const url = `https://api.cloudinary.com/v1_1/${cloudName}/upload`

      const response = await http.postAuthed('/auth-cloudinary')
      const authParams = response.data

      const xhr = new XMLHttpRequest()
      // event listeners must be hooked before open method is called
      xhr.upload.addEventListener('progress', this.onProgress)

      xhr.open('POST', url)
      this.uploadInProgress = true

      const formData = new FormData()
      formData.set('file', file)
      toPairs(authParams).forEach(([key, val]) => {
        formData.set(key, val)
      })

      xhr.send(formData._blob()) // need that ._blob() call as FormData is a polyfill
      xhr.onreadystatechange = function () {
        console.log(' => xhr.readyState:', xhr.readyState)
        if (xhr.readyState === XMLHttpRequest.DONE) {
          self.uploadInProgress = false
          xhr.removeEventListener('progress', self.onProgress)
          const parsedData = JSON.parse(xhr.response)
          self.$emit('upload-done', {
            file: file,
            formData: formData,
            xhr: xhr,
            data: parsedData,
            url: parsedData.url,
          })
          if (self.crop.enabled) {
            self.onDialogClose()
          }
          self.percent = 0
          self.$el.querySelector('.vb-uploader-input').value = null
        }
      }
    },
    onProgress(event) {
      this.$emit('progress', event)
      if (event.lengthComputable && event.total > 0) {
        const percent = (100 * event.loaded) / event.total
        this.percent = Number(percent.toFixed(0))
      }
    },
    async onDrop(event) {
      if (!this.enabled) return
      await this.stopDrag()

      const file = event.dataTransfer.files[0]

      if (this.crop.enabled) {
        await this.loadImageToCrop(file)
        return
      }

      await this.uploadFile(file)
    },
    startDrag() {
      if (!this.enabled) return
      this.isDragging = true
    },
    stopDrag() {
      if (!this.enabled) return
      this.isDragging = false
    },
    cancelUploading() {
      this.onDialogClose()
      this.percent = 0
    },
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.vb-uploader-input {
  display: none !important;
}

.vb-uploader {
  position: relative;
  border-radius: 3px;
  clear: both;

  &.is-dragging {
    background-color: lighten($yellow, 25%);
    border: 2px dashed $yellow;
  }

  .bg {
    display: none;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(255, 255, 255, 0.8);
    z-index: 1;
  }
  svg {
    position: absolute;
    top: 0;
    left: 0;
  }

  .circle-progress {
    display: none;
    position: absolute;
    top: 50%;
    margin-top: -20px;
    left: 50%;
    margin-left: -20px;
    text-align: center;
    z-index: 2000;
  }

  .hover-icon {
    display: none;
    position: absolute;
    top: 30%;
    left: 50%;
    margin-left: -18px;
    text-align: center;
    z-index: 2;
    font-size: 21px;
    padding: 3px 9px;
    border-radius: 50%;
    background-color: $black;
    color: $bright;
  }

  &.enabled:hover {
    cursor: pointer;
    opacity: 0.8;

    .hover-icon {
      display: block;
      opacity: 0.9;
    }
  }

  &.upload-in-progress {
    .bg {
      display: block;
    }

    .circle-progress {
      display: block;
    }
  }
}
</style>
